/**
 * An in-memory provider. THE CONTRACT PROVING-GROUND, NOT THE PRODUCT SYNC.
 *
 * It exists so the sync engine can be proven before any real cloud is involved.
 * A provider connector written against a live Supabase project is a provider
 * whose bugs are indistinguishable from its host's outages; this one has no
 * host, so a failing test is a failing test.
 *
 * It is also deliberately NASTY about the things real providers are nasty about,
 * because a contract suite that only tests a polite provider proves nothing:
 *
 *  - `pushChanges` is idempotent on `changeId`, and a duplicate is NOT counted
 *    as an acceptance of new work;
 *  - `pullChanges` really does return `requiresSnapshot` when the cursor is
 *    older than the retained log, instead of quietly resending everything;
 *  - the log is genuinely compacted, so the "long-offline device" test exercises
 *    the recovery path rather than a path that never triggers;
 *  - it can be switched offline, and offline means the same thing a real
 *    network means: the call rejects and NOTHING was recorded.
 *
 * Change history and acknowledgements are per business, so Business A and
 * Business B in the same "cloud" cannot see each other.
 */
import type { BackupProvider, ProviderStatus, SkippedChange, SyncProvider } from './types';
import { ProviderError } from './types';
import type { ProviderId } from '@domain/sync/changeModel';
import {
  FILE_STORAGE,
  MANAGED_ROW_STORE,
  type SyncCapabilities,
  type SyncChange,
  type SyncCursor,
} from '@domain/sync/changeModel';
import type { DeviceAcknowledgement } from '@domain/sync/retention';
import { mayCompact } from '@domain/sync/retention';

interface StoredChange {
  readonly cursor: string;
  readonly change: SyncChange;
}

interface BusinessLog {
  /** Append-only, compacted by `oldestNeededCursor`. */
  changes: StoredChange[];
  /** Every change ever seen, for the snapshot. Compaction does not touch this. */
  readonly allChanges: Map<string, SyncChange>;
  acks: Map<string, DeviceAcknowledgement>;
  nextSeq: number;
}

/** A deterministic, monotonically increasing cursor. */
function cursorFor(seq: number): string {
  return seq.toString().padStart(12, '0');
}

function parseCursor(token: string): number {
  return Number.parseInt(token, 10);
}

/**
 * Are these two changes the SAME mutation?
 *
 * Only if they say the same thing. A `changeId` is supposed to identify one
 * mutation, so a repeat with a different payload means an id was reused — and the
 * id alone cannot tell that apart from a retry.
 *
 * Compared through a canonical serialisation, because key ORDER in a payload is an
 * artefact of whatever object produced it and is not part of its meaning. Keys with
 * `undefined` values are dropped, which is what makes a DELETE's absent payload
 * compare equal to another DELETE's absent payload.
 */
function sameContent(a: SyncChange, b: SyncChange): boolean {
  return stableJson(a) === stableJson(b);
}

function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(',')}}`;
}

export class FakeSyncProvider implements SyncProvider {
  readonly id: ProviderId;
  readonly label: string;
  /** Which capability set to advertise. Tests exercise both. */
  private readonly caps: SyncCapabilities;

  private readonly logs = new Map<string, BusinessLog>();
  private connected = true;
  private reachable = true;
  /** Track total acknowledge() calls across all businesses for test assertions. */
  private ackCount = 0;
  /** When set, fetchSnapshot() returns these changes instead of deriving from allChanges. */
  private snapshotOverride: Map<string, SyncChange[]> | null = null;
  /** When set, the next call rejects with this and records nothing. */
  private failNextWith: { message: string; retryable: boolean } | null = null;
  /** Pushes that "succeeded" server-side but whose response was lost. */
  private swallowNextPush = false;

  constructor(
    opts: {
      id?: ProviderId;
      label?: string;
      capabilities?: 'managed' | 'file';
    } = {},
  ) {
    this.id = opts.id ?? 'fake';
    this.label = opts.label ?? 'In-memory test provider';
    this.caps = opts.capabilities === 'file' ? FILE_STORAGE : MANAGED_ROW_STORE;
  }

  capabilities(): SyncCapabilities {
    return this.caps;
  }

  // ── Test controls. Not part of any contract. ──────────────────────────────

  /** Take the network away. The next call rejects and changes nothing. */
  setOffline(offline: boolean): void {
    this.reachable = !offline;
    if (offline) this.connected = false;
  }

  setConnected(connected: boolean): void {
    this.connected = connected;
  }

  /** Make exactly one call fail, simulating a transient network error. */
  failNext(message = 'simulated failure', retryable = true): void {
    this.failNextWith = { message, retryable };
  }

  /**
   * Override the snapshot contents returned by `fetchSnapshot` for a business.
   *
   * Use this to simulate a missing or mismatched business row — the exact
   * scenario the `sync_prune_compacted` bug would cause. Pass an array that
   * omits the business entity, or includes a business change with a different
   * businessId/entityId.
   */
  setSnapshotOverride(businessId: string, changes: SyncChange[]): void {
    if (!this.snapshotOverride) this.snapshotOverride = new Map();
    this.snapshotOverride.set(businessId, [...changes]);
  }

  /** Clear any snapshot override. */
  clearSnapshotOverride(businessId: string): void {
    this.snapshotOverride?.delete(businessId);
  }

  /**
   * Simulate the dangerous case: the write LANDS, the response is lost.
   *
   * This is the scenario idempotency exists for, and it cannot be tested with a
   * provider that just fails cleanly — a clean failure records nothing, so a
   * retry has nothing to deduplicate.
   */
  swallowNextPushResponse(): void {
    this.swallowNextPush = true;
  }

  /** How many changes the log holds, for assertions about compaction. */
  logLength(businessId: string): number {
    return this.log(businessId).changes.length;
  }

  /**
   * How many DISTINCT changes this provider has ever recorded, ignoring
   * compaction.
   *
   * This is the honest measure of idempotency: a log can shrink because
   * compaction ran, so its length says nothing about whether a retry duplicated
   * anything. `allChanges.size` does.
   */
  distinctChangeCount(businessId: string): number {
    return this.log(businessId).allChanges.size;
  }

  /** Total number of acknowledge() calls across all businesses. */
  totalAckCount(): number {
    return this.ackCount;
  }

  /** Reset the acknowledge counter (for test setup accounting). */
  resetAckCount(): void {
    this.ackCount = 0;
  }

  /**
   * Simulate server-side pruning: remove a specific change by its changeId.
   *
   * This mirrors the effect of the buggy `sync_prune_compacted` deleting a
   * business entity change. After pruning, the provider's `allChanges` no
   * longer contains the change, so a subsequent push with the same changeId
   * is treated as NEW (not a duplicate), and `fetchSnapshot` omits it.
   *
   * Does NOT decrement `nextSeq` — cursors remain monotonic, as a real
   * Postgres DELETE does not reclaim sequence values.
   */
  pruneChangeById(businessId: string, changeId: string): boolean {
    const log = this.log(businessId);
    const deleted = log.allChanges.delete(changeId);
    if (deleted) {
      log.changes = log.changes.filter((c) => c.change.changeId !== changeId);
    }
    return deleted;
  }

  // ── Storage. ───────────────────────────────────────────────────────────

  private log(businessId: string): BusinessLog {
    let log = this.logs.get(businessId);
    if (!log) {
      log = { changes: [], allChanges: new Map(), acks: new Map(), nextSeq: 1 };
      this.logs.set(businessId, log);
    }
    return log;
  }

  private guard(): void {
    if (!this.reachable) {
      throw new ProviderError('UNREACHABLE', 'provider is offline', true);
    }
    if (this.failNextWith) {
      const { message, retryable } = this.failNextWith;
      this.failNextWith = null;
      // Nothing is recorded. A failed call leaves no trace.
      throw new ProviderError('UNKNOWN', message, retryable);
    }
    if (!this.connected) {
      throw new ProviderError('NOT_AUTHENTICATED', 'not connected', false);
    }
  }

  async getStatus(): Promise<ProviderStatus> {
    if (!this.reachable) {
      return { state: 'error', authorisedBusinessIds: [], message: 'offline' };
    }
    if (!this.connected) {
      return { state: 'unauthenticated', authorisedBusinessIds: [] };
    }
    return {
      state: 'connected',
      accountLabel: 'in-memory',
      authorisedBusinessIds: [...this.logs.keys()],
    };
  }

  async isReachable(): Promise<boolean> {
    return this.reachable;
  }

  // ── SyncProvider. ──────────────────────────────────────────────────────

  async pushChanges(input: {
    businessId: string;
    deviceId: string;
    changes: readonly SyncChange[];
  }): Promise<{ accepted: readonly string[] }> {
    this.guard();

    const log = this.log(input.businessId);
    const accepted: string[] = [];

    for (const change of input.changes) {
      // A change for another business is REFUSED, not filed under this one.
      // Without this a provider that trusted a `businessId` parameter would let
      // any device write into any business's log.
      if (change.businessId !== input.businessId) {
        throw new ProviderError(
          'FORBIDDEN',
          `change ${change.changeId} is for ${change.businessId}, not ${input.businessId}`,
          false,
        );
      }

      // THE IDEMPOTENCY RULE, and it compares CONTENT rather than the id alone.
      //
      // A `changeId` identifies ONE originating mutation. It is minted once, at
      // the local write, stored beside the payload, and re-sent unchanged on every
      // retry — so a repeat carrying the same content IS that retry: acknowledge
      // it again and append nothing.
      //
      // A repeat carrying DIFFERENT content is not a retry at all: two different
      // mutations have been given one id. Both possible "fixes" are wrong — keeping
      // the first is last-write-wins, and keeping the second loses the first — so
      // this refuses loudly instead, and the client that did it gets found rather
      // than believed.
      const seen = log.allChanges.get(change.changeId);
      if (seen) {
        if (!sameContent(seen, change)) {
          throw new ProviderError(
            'REJECTED',
            `changeId ${change.changeId} was already filed by a different mutation; ` +
              'refusing to keep one and drop the other',
            false,
          );
        }
        accepted.push(change.changeId);
        continue;
      }

      const cursor = cursorFor(log.nextSeq++);
      log.allChanges.set(change.changeId, change);
      log.changes.push({ cursor, change });
      accepted.push(change.changeId);
    }

    // The write landed; the client never finds out. A retry must be a no-op.
    if (this.swallowNextPush) {
      this.swallowNextPush = false;
      throw new ProviderError('UNREACHABLE', 'response lost after write', true);
    }

    return { accepted };
  }

  async pullChanges(input: {
    businessId: string;
    deviceId: string;
    cursor: SyncCursor | null;
    limit: number;
  }): Promise<{
    changes: readonly SyncChange[];
    nextCursor: SyncCursor | null;
    requiresSnapshot: boolean;
    skipped: readonly SkippedChange[];
  }> {
    this.guard();
    const log = this.log(input.businessId);
    // A null cursor means "I have nothing", which is position zero — the same
    // place a long-offline device starts. It is NOT exempt from the check below.
    const from = input.cursor ? parseCursor(input.cursor.token) : 0;

    // The oldest thing the log still holds. A cursor behind it cannot be
    // honoured incrementally, and the honest answer is to say so.
    //
    // Note there is no `input.cursor &&` guard here. A brand-new device with a
    // null cursor is the MOST out-of-date device there is, and exempting it
    // made it receive an empty result with `requiresSnapshot: false` — a device
    // that silently synced nothing and reported success. That is worse than an
    // error: the user believes two machines agree when they do not.
    const oldest = parseCursor(log.changes.at(0)?.cursor ?? cursorFor(log.nextSeq));
    if (from < oldest - 1) {
      return { changes: [], nextCursor: null, requiresSnapshot: true, skipped: [] };
    }

    const window = log.changes.filter((c) => parseCursor(c.cursor) > from).slice(0, input.limit);
    const last = window.at(-1)?.cursor ?? null;
    return {
      changes: window.map((c) => c.change),
      nextCursor: last ? { token: last } : input.cursor,
      requiresSnapshot: false,
      // This provider stores typed changes, so it cannot hold an unreadable row.
      // The empty list is still returned, because "nothing skipped" and "does
      // not report skips" must not look the same to the engine.
      skipped: [],
    };
  }

  async fetchSnapshot(input: { businessId: string }): Promise<{
    changes: readonly SyncChange[];
    cursor: SyncCursor;
    skipped: readonly SkippedChange[];
  }> {
    this.guard();
    const log = this.log(input.businessId);
    // Allow test override for simulating missing/mismatched business rows.
    const override = this.snapshotOverride?.get(input.businessId);
    if (override) {
      const head = log.nextSeq - 1;
      return { changes: override, cursor: { token: cursorFor(head) }, skipped: [] };
    }
    // Only the LATEST change per entity — a snapshot is current state, not a
    // replay. Sending the whole history would be both slower and wrong.
    const latest = new Map<string, SyncChange>();
    for (const change of log.allChanges.values()) {
      const key = `${change.entityType}:${change.entityId}`;
      const existing = latest.get(key);
      if (!existing || existing.version <= change.version) latest.set(key, change);
    }
    const head = log.nextSeq - 1;
    return {
      changes: [...latest.values()],
      cursor: { token: cursorFor(head) },
      skipped: [],
    };
  }

  async acknowledge(input: {
    businessId: string;
    deviceId: string;
    cursor: SyncCursor;
  }): Promise<void> {
    this.ackCount++;
    this.guard();
    const log = this.log(input.businessId);
    const previous = log.acks.get(input.deviceId);
    // Never move a device BACKWARDS. A stale acknowledgement from a retrying
    // request would un-pin changes that were already safe to compact.
    if (previous && parseCursor(previous.cursor) > parseCursor(input.cursor.token)) return;
    log.acks.set(input.deviceId, {
      businessId: input.businessId,
      deviceId: input.deviceId,
      cursor: input.cursor.token,
      lastSeenAt: new Date().toISOString(),
    });
    this.compact(log);
  }

  async oldestNeededCursor(input: { businessId: string }): Promise<SyncCursor | null> {
    const log = this.log(input.businessId);
    if (log.acks.size === 0) return null;
    const cursors = [...log.acks.values()].map((a) => a.cursor);
    const min = cursors.reduce((a, b) => (a < b ? a : b));
    return { token: min };
  }

  /**
   * Compaction, applied on acknowledgement.
   *
   * Real compaction, not a simulation: entries genuinely disappear from
   * `changes`. A test claiming to prove long-offline recovery against a log that
   * never shrinks has proven nothing, and the recovery branch would sit
   * unexecuted until a real customer hit it.
   */
  private compact(log: BusinessLog): void {
    if (log.acks.size === 0) return;
    const cursors = [...log.acks.values()].map((a) => a.cursor);
    const slowest = cursors.reduce((a, b) => (a < b ? a : b));
    log.changes = log.changes.filter(
      (c) => !mayCompact(c.cursor, { compactBelow: slowest, ignoredStaleDeviceIds: [] }),
    );
  }

  async checkBusinessRowExists(input: {
    readonly businessId: string;
  }): Promise<{ exists: boolean; totalChanges: number; cursor: string } | null> {
    this.guard();
    const log = this.log(input.businessId);

    // Mirror sync_fetch_snapshot: latest version per entity.
    const latest = new Map<string, SyncChange>();
    for (const change of log.allChanges.values()) {
      const key = `${change.entityType}:${change.entityId}`;
      const existing = latest.get(key);
      if (!existing || existing.version <= change.version) latest.set(key, change);
    }

    const businessChange = [...latest.values()].find(
      (c) => c.entityType === 'business' && c.entityId === input.businessId,
    );

    return {
      exists: businessChange !== undefined,
      totalChanges: latest.size,
      cursor: cursorFor(log.nextSeq - 1),
    };
  }
}

/** The fake backup destination. Stores the exact text it was given. */
export class FakeBackupProvider implements BackupProvider {
  readonly id: ProviderId;
  readonly label = 'In-memory test provider';
  readonly canDelete = true;

  constructor(id: ProviderId = 'fake') {
    this.id = id;
  }

  private readonly files = new Map<
    string,
    { json: string; updatedAt: string; sizeBytes: number }
  >();
  private reachable = true;

  setOffline(offline: boolean): void {
    this.reachable = !offline;
  }

  private guard(): void {
    if (!this.reachable) throw new ProviderError('UNREACHABLE', 'provider is offline', true);
  }

  async getStatus(): Promise<ProviderStatus> {
    return this.reachable
      ? { state: 'connected', accountLabel: 'in-memory', authorisedBusinessIds: [] }
      : { state: 'error', authorisedBusinessIds: [], message: 'offline' };
  }

  async isReachable(): Promise<boolean> {
    return this.reachable;
  }

  async uploadBackup(input: {
    fileName: string;
    json: string;
    takenAt: string;
  }): Promise<{ remoteName: string }> {
    this.guard();
    this.files.set(input.fileName, {
      json: input.json,
      updatedAt: input.takenAt,
      sizeBytes: input.json.length,
    });
    return { remoteName: input.fileName };
  }

  async downloadBackup(input: { remoteName: string }): Promise<{ json: string }> {
    this.guard();
    const file = this.files.get(input.remoteName);
    if (!file) {
      throw new ProviderError('REJECTED', `no backup named ${input.remoteName}`, false);
    }
    return { json: file.json };
  }

  async listBackups() {
    this.guard();
    return [...this.files.entries()].map(([remoteName, f]) => ({
      remoteName,
      sizeBytes: f.sizeBytes,
      updatedAt: f.updatedAt,
    }));
  }

  async deleteBackup(input: { remoteName: string }): Promise<void> {
    this.guard();
    if (!this.files.delete(input.remoteName)) {
      throw new ProviderError('REJECTED', `no backup named ${input.remoteName}`, false);
    }
  }
}
